// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import api models.
import {
	PossibleBaseData,
} from '@app/models/api';
// Import props.
import {
	InputDataSelectionListProps,
	InputFormProps,
} from '@app/props';

interface UseInputDataSelectionListParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	value?: string;
	showValidation?: boolean;
}

interface UseInputDataSelectionListWithPossibleDataSelectionsParameters extends UseInputDataSelectionListParameters {
	possibleDataSelections: PossibleBaseData[]
}

interface UseInputDataSelectionListResults extends UseInputFormResult<string | undefined, InputDataSelectionListProps> {
	getSelectedValueDisplayName: () => string | undefined;
}

const useInputDataSelectionList = (params: UseInputDataSelectionListWithPossibleDataSelectionsParameters): UseInputDataSelectionListResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyInputDataSelectionListProps = (): InputDataSelectionListProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the perils selection list props.
		return {
			...formProps,
			value: other.value,
			possibleDataSelections: params.possibleDataSelections,
			onSelect: other.setInput,
		}
	};

	/**
	 * Returns the display name version of the selected value.
	 */
	const getSelectedValueDisplayName = (): string | undefined => {
		let selectedValueDisplayName: string | undefined = undefined;

		const selectedValueCode: string | undefined = other.value;
		if (selectedValueCode !== undefined) {
			selectedValueDisplayName = params.possibleDataSelections.find((possibleDataSelection: PossibleBaseData) => possibleDataSelection.code === selectedValueCode)?.displayName ?? undefined;
		}
		return selectedValueDisplayName;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if the selected value is a valid.
	 * @param selectedValue
	 */
	const isSelectedDataValid = (selectedValue: string | undefined): boolean => {
		// Make sure that any selected possible data is a valid option.
		let selectedPossibleData: PossibleBaseData | undefined = params.possibleDataSelections.find((possibleDataSelection: PossibleBaseData) => {
			return possibleDataSelection.code !== null
				&& possibleDataSelection.code === selectedValue;
		});

		return selectedPossibleData !== undefined
	}


	/**
	 * Validates the selected value.
	 * @param selectedValue
	 */
	const validateDataSelection = (selectedValue: string | undefined, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& String.isNullOrWhiteSpace(selectedValue)) {
			// Make sure there is a selected value.
			response = new ValidatorResponse(false, 'An answer is required.');
		}
		else if (!String.isNullOrWhiteSpace(selectedValue)
			&& !isSelectedDataValid(selectedValue)) {
			// Make sure the selected value is valid.
			response = new ValidatorResponse(false, 'Answer is invalid.');
		}

		return response;
	}

	/**
	 * Sets up the intial value of the input.
	 * @param initialValue
	 */
	const setupInitialValue = (initialValue: string | undefined): string | undefined => {
		let updatedInitialValue: string | undefined = initialValue;
		if (updatedInitialValue !== undefined
			&& !isSelectedDataValid(updatedInitialValue)) {
			updatedInitialValue = undefined;
		}

		return updatedInitialValue;
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'data selection list' input.
	const { applyProps, ...other } = useInputForm<string | undefined>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: setupInitialValue(params.value),
		showValidation: params.showValidation,
		validators: [validateDataSelection],
	});

	// #endregion variables


	return {
		applyProps: applyInputDataSelectionListProps,
		getSelectedValueDisplayName: getSelectedValueDisplayName,
		...other,
	};
}

// Export parameters.
export {
	UseInputDataSelectionListParameters,
	UseInputDataSelectionListWithPossibleDataSelectionsParameters,
}

// Export results.
export {
	UseInputDataSelectionListResults,
}

export default useInputDataSelectionList;